import React, { useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import lang from '@/lang'
import config from '@/config'
import ThemeInterface from '@/theme-interface'
import TextInput from '@/modules/base/components/inputs/text-input/text-input'
import ZipCodeInput from '@/modules/base/components/inputs/zipCode-input/zipCode-input'
import CountryInput from '@/modules/base/components/inputs/country-input/country-input'
import SalutationInput from '@/modules/base/components/inputs/salutation-input/salutation-input'
import TitleInput from '@/modules/base/components/inputs/title-input/title-input'
import RadioInput from '@/modules/base/components/inputs/radio-input/radio-input'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import { GenericListsService } from '@/modules/base/components/generics/lists/genericListsService'
import SupplierService from '@/modules/supplier/supplierService'
import CustomerService from '@/modules/customer/customerService'
import {
  changeReviewPayeeRole,
  createReviewPayeeState,
  getReviewPayeeDisplayName,
  ReviewPayeeEdit,
  ReviewPayeeInput,
  ReviewPayeeState,
  selectReviewPayee,
  updateReviewPayee,
  validateReviewPayee,
} from './review-payee-state'
import { ReviewPayeeRole } from '@/modules/document/types'

// FLAG:useDocuAi — which way the analysed invoice points, derived server-side from the
// two printed parties vs the tenant's own company. Display-only: it decides which roles
// are PLAUSIBLE, never which one is used. 'unknown' leaves every role freely selectable;
// until direction detection is reliable it deliberately renders no explanatory notice.
export type DocumentDirection = 'incoming' | 'outgoing' | 'unknown'

interface ReviewPayeeSectionProps {
  value: ReviewPayeeState
  onChange: (value: ReviewPayeeState) => void
  readOnly?: boolean
  documentDirection?: DocumentDirection
  // FLAG:useDocuAi — the seller and buyer the document names, used ONLY to seed the form
  // when the reviewer switches between a supplier and a customer role. Absent prop = no
  // seeds, and the role switch keeps the current data exactly as it did before.
  recognizedParties?: {
    supplier?: ReviewPayeeInput | null
    buyer?: ReviewPayeeInput | null
  } | null
  // FLAG:useDocuAi — die gedruckte USt-IdNr liegt auf MEHREREN Lieferanten der Stammdaten, sie
  // identifiziert also niemanden mehr (api supplierIdentity.findSupplierByVatId). Der Prüfer sieht
  // sonst nur, dass kein oder ein unerwarteter Lieferant zugeordnet wurde, und erfährt den Grund
  // nie. Absent/false = unverändertes Verhalten.
  vatIdAmbiguous?: boolean
}

interface DirectoryOption {
  value: number
  label: string
  labelPrefix?: string | number
  labelSuffix?: string
  type: 'supplier' | 'customer'
}

interface ReviewRadioInputProps {
  dataQsId: string
  name: string
  value: string
  options: Array<{ value: string, label: string }>
  disabled?: boolean
  customClass?: string
  onChange: (value: string, name: string) => void
}

// These shared controls are JavaScript modules whose inferred PropTypes declarations
// omit supported runtime props. Keep that legacy typing gap at this local boundary.
const ReviewRadioInput = RadioInput as unknown as React.ComponentType<ReviewRadioInputProps>

const payeeLang = lang.documents.reviewPayee

// One vocabulary for the selector and for the prose, so the sentence about what the save
// will create names exactly the role the reviewer just clicked (never "master record"/
// "business partner"). Annotated so the lang block stays exhaustive over the roles.
const ROLE_LABELS: Record<ReviewPayeeRole, string> = payeeLang.roles

const ROLE_ORDER: ReviewPayeeRole[] = ['supplier', 'companyCustomer', 'privateCustomer']

// Which of the two parties the document names a role refers to. An invoice has a seller and
// a buyer; the payee snapshot starts as the SELLER, so choosing a customer role means the
// counterparty is the BUYER — and the form has to show that party's data, not the vendor's.
type PartyGroup = 'seller' | 'buyer'
const groupOfRole = (role: ReviewPayeeRole): PartyGroup => role === 'supplier' ? 'seller' : 'buyer'

// An invoice billed TO us has a supplier on the other side; one we issued has a customer,
// which may be a company or a private person. Roles outside the derived direction stay
// SELECTABLE — a reviewer who knows better must be able to overrule a name/zip match —
// but they are labelled so the reason is visible instead of a bare required-field error.
const ROLES_BY_DIRECTION: Record<DocumentDirection, ReviewPayeeRole[] | null> = {
  incoming: ['supplier'],
  outgoing: ['companyCustomer', 'privateCustomer'],
  unknown: null,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    width: '100%',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: theme.fontSizes?.medium ?? 15,
    color: theme.black,
    marginBottom: 16,
  },
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '12px 14px',
    borderRadius: theme.borderRadius,
    fontSize: theme.fontSizes?.small ?? 12,
    lineHeight: 1.45,
    marginBottom: 16,
  },
  bannerInfo: {
    background: theme.lightBlueBackground,
    color: theme.black,
  },
  bannerWarning: {
    background: theme.yellowBackground,
    color: theme.black,
  },
  bannerIcon: {
    fontSize: 18,
    flexShrink: 0,
  },
  bannerIconInfo: {
    color: theme.primary,
  },
  bannerIconWarning: {
    color: theme.yellow,
  },
  card: {
    border: `1px solid ${theme.borderColor}`,
    borderRadius: theme.borderRadius,
    background: theme.white,
    marginBottom: 20,
  },
  // The fields are composed here rather than reusing the shared address-form blocks: those
  // are laid out for the WIDE legacy address dialog (two fields per row, negative gutters,
  // first/last-of-type paddings) and inside this narrow card their rows ended up indented
  // differently from one another. One grid, one left edge.
  cardBody: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  fieldPair: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
  },
  // Resting state: the recognised contact as plain text, with the pencil in its own
  // cell on the right. Clicking anywhere on it opens the form (click-to-edit).
  display: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    cursor: 'pointer',
  },
  displayReadOnly: {
    cursor: 'default',
  },
  displayLines: {
    flex: 1,
    minWidth: 0,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: theme.fontSizes?.default ?? 14,
    color: theme.black,
  },
  displayPlaceholder: {
    color: theme.grey,
  },
  displayError: {
    color: theme.red,
    fontWeight: 600,
    fontSize: theme.fontSizes?.small ?? 12,
  },
  displayEditCell: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    borderLeft: `1px solid ${theme.borderColor}`,
    color: theme.grey,
  },
  assignLabel: {
    fontSize: theme.fontSizes?.small ?? 12,
    color: theme.black,
    marginBottom: 8,
  },
  assignHint: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    fontSize: theme.fontSizes?.small ?? 12,
    color: theme.grey,
  },
  assignHintIcon: {
    fontSize: 15,
  },
  mismatch: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 12px',
    borderRadius: theme.borderRadius,
    background: theme.lightBlueBackground,
    fontSize: theme.fontSizes?.small ?? 12,
    lineHeight: 1.45,
    marginBottom: 16,
  },
}))

const loadDirectoryOptions = async(search: string, loadedOptions: DirectoryOption[] = []) => {
  const filterModel = search
    ? {
      name: { filter: search, type: 'contains', filterType: 'text' },
      number: { filter: search, type: 'contains', filterType: 'text' },
      operator: 'OR',
    }
    : undefined
  const { data = [], meta = { count: 0 } } = await GenericListsService.getCustomerAndSuppliersSsr({
    startRow: loadedOptions.length,
    endRow: loadedOptions.length + 10,
    filterModel,
  })
  const options = data.map(({ baseDataId, name, number, type }) => ({
    value: baseDataId,
    labelPrefix: number,
    labelSuffix: type === 'customer' ? '(K)' : '(L)',
    label: name,
    type,
  }))

  return {
    options,
    hasMore: loadedOptions.length + options.length < meta.count,
  }
}

const getDirectoryAddress = (party, type: DirectoryOption['type']) => {
  if (type === 'customer' && party.customerDefaultAddress?.billingAddress) {
    return party.customerDefaultAddress.billingAddress
  }
  if (party.defaultBillingAddressId) {
    return party.addresses?.find(address => address.id === party.defaultBillingAddressId) ?? {}
  }
  return party.addresses?.find(address => address.isDefault) ?? party.addresses?.[0] ?? {}
}

const buildDirectorySelection = (party, type: DirectoryOption['type']) => {
  const address = getDirectoryAddress(party, type)
  const role: ReviewPayeeRole = type === 'supplier'
    ? 'supplier'
    : party.kind === 'person' ? 'privateCustomer' : 'companyCustomer'

  return {
    version: 1 as const,
    role,
    directoryRef: { type, id: party.id },
    companyName: party.companyName ?? (role === 'privateCustomer' ? '' : party.name),
    companyNameAffix: party.companyNameAffix,
    salutation: party.salutation,
    title: party.title,
    firstName: party.firstName,
    lastName: party.lastName,
    address: {
      street: address.street,
      zipCode: address.zipCode ?? address.zip,
      city: address.city,
      countryIso: address.countryIso,
    },
  }
}

const ReviewPayeeSection = ({
  value,
  onChange,
  readOnly = false,
  documentDirection,
  recognizedParties,
  vatIdAmbiguous = false,
}: ReviewPayeeSectionProps) => {
  const classes = useStyles()
  // Purely a display/edit toggle. The contact itself is persisted by the ONE review save
  // together with everything else, so edits apply immediately — there is no local commit
  // step and therefore nothing to confirm or cancel.
  const [expanded, setExpanded] = useState(false)
  const isEditing = expanded && !readOnly
  // What the reviewer last had for the party group they left, so a round trip
  // (supplier → customer → supplier) gives their own corrections back rather than the
  // raw extraction.
  const [partyMemo, setPartyMemo] = useState<Partial<Record<PartyGroup, ReviewPayeeState>>>({})

  // No prop = no direction was derived at all (a caller that predates this, or a legacy
  // analysis). An explicit 'unknown' also leaves all roles available, without a direction
  // notice until the detection is reliable.
  const plausibleRoles = documentDirection ? ROLES_BY_DIRECTION[documentDirection] : null
  const directionHint = documentDirection === 'incoming' || documentDirection === 'outgoing'
    ? payeeLang.directionHint[documentDirection]
    : ''
  const roleFitsDirection = (role: ReviewPayeeRole) => !plausibleRoles || plausibleRoles.includes(role)
  // The company name the document actually carries. Under the privateCustomer role it is
  // neither shown nor validated, so switching there silently hid a recognised company and
  // demanded a Nachname the document can never supply — name that instead of staying mute.
  const recognisedCompany = value.companyName?.trim()
  const validation = validateReviewPayee(value)
  const companyNameError = validation.errors.companyName ? payeeLang.companyNameRequired : ''
  const lastNameError = validation.errors.lastName ? payeeLang.lastNameRequired : ''
  const displayName = getReviewPayeeDisplayName(value)
  const selectedOption = value.directoryRef
    ? {
      value: value.directoryRef.id,
      label: displayName || payeeLang.noName,
      type: value.directoryRef.type,
    }
    : null

  const updateIdentity = (fieldValue: string, name: keyof ReviewPayeeEdit) => {
    onChange(updateReviewPayee(value, { [name]: fieldValue }))
  }
  const updateAddress = (address: Partial<ReviewPayeeState['address']>) => {
    onChange(updateReviewPayee(value, { address }))
  }

  // The party that IS us is never a useful seed: on an incoming invoice the buyer is the
  // tenant's own company, on an outgoing one the seller is. Seeding it put the user's own
  // name and address into the payee form, and saving that creates a master record for
  // themselves ("the supplier resolves to me"). Where the direction is known we therefore
  // refuse that side; 'unknown' keeps both, because there we genuinely cannot tell which
  // side is us and the reviewer decides.
  const seedIsOwnCompany = (group: PartyGroup) => (
    (group === 'buyer' && documentDirection === 'incoming')
    || (group === 'seller' && documentDirection === 'outgoing')
  )

  const seedForGroup = (group: PartyGroup, role: ReviewPayeeRole) => {
    if (seedIsOwnCompany(group)) return null
    const seed = group === 'buyer' ? recognizedParties?.buyer : recognizedParties?.supplier
    // Nothing recognised for that side (the document named only one party) — keep what is
    // on screen instead of blanking the form.
    if (!seed) return null
    return createReviewPayeeState({ ...seed, role, directoryRef: null, updateDirectory: false })
  }

  const selectRole = (nextRole: string) => {
    const role = nextRole as ReviewPayeeRole
    if (role === value.role) return

    const fromGroup = groupOfRole(value.role)
    const toGroup = groupOfRole(role)
    // Company customer ↔ private customer is the same party in a different legal shape: the data stays.
    if (fromGroup === toGroup) {
      onChange(changeReviewPayeeRole(value, role))
      return
    }

    setPartyMemo(memo => ({ ...memo, [fromGroup]: value }))
    const remembered = partyMemo[toGroup]
    onChange(remembered
      ? changeReviewPayeeRole(remembered, role)
      : seedForGroup(toGroup, role) ?? changeReviewPayeeRole(value, role))
  }

  const handleDirectoryChange = async(option?: DirectoryOption) => {
    if (!option?.value || !option.type) {
      onChange({ ...value, directoryRef: null, updateDirectory: false })
      return
    }

    const party = option.type === 'supplier'
      ? await SupplierService.getById(option.value)
      : await CustomerService.getById(option.value)
    const selection = buildDirectorySelection(party, option.type)
    onChange(selectReviewPayee(value, selection))
  }

  // stopPropagation: the review runs inside a drawer that closes on Escape, and the
  // surrounding form must not see the key either.
  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    setExpanded(false)
  }

  const willUpdateDirectory = !!value.directoryRef && value.updateDirectory
  // FLAG:useDocuAi — eine mehrdeutige USt-IdNr schlägt jede der drei gewohnten Aussagen, weil sie
  // erklärt, warum die Zuordnung so aussieht, wie sie aussieht: die Nummer liegt auf mehreren
  // Lieferanten, also hat sie nicht entschieden. Nur für die Lieferantenrolle — die Verkäufer-
  // USt-IdNr kann nie einen Kunden bezeichnen. Ohne das Flag bleibt alles wie vorher.
  const banner = vatIdAmbiguous && value.role === 'supplier'
    ? {
      variant: 'warning' as const,
      headline: payeeLang.vatIdAmbiguousHeadline,
      body: payeeLang.vatIdAmbiguousBody,
    }
    : value.directoryRef
      ? willUpdateDirectory
        ? {
          variant: 'warning' as const,
          headline: payeeLang.assigned(ROLE_LABELS[value.role]),
          body: payeeLang.bodyWillUpdate,
        }
        : {
          variant: 'info' as const,
          headline: payeeLang.assigned(ROLE_LABELS[value.role]),
          body: payeeLang.bodyFromDirectory,
        }
      : {
        variant: 'info' as const,
        headline: payeeLang.assignedNew(ROLE_LABELS[value.role]),
        body: payeeLang.bodyWillCreate,
      }

  const addressLines = [
    value.address.street?.trim(),
    [value.address.zipCode?.trim(), value.address.city?.trim()].filter(Boolean).join(' '),
    // Only worth a line when it is not the default country the form assumes.
    value.address.countryIso && value.address.countryIso !== 'DE' ? value.address.countryIso : '',
  ].filter(Boolean)

  const identityFields = value.role === 'privateCustomer'
    ? (
      <>
        <div className={classes.fieldPair}>
          <SalutationInput
            dataQsId='reviewPayee-salutation'
            value={value.salutation}
            useFormik={false}
            onChange={(option: string) => updateIdentity(option, 'salutation')}
          />
          <TitleInput
            dataQsId='reviewPayee-title'
            value={value.title}
            useFormik={false}
            onChange={(option: string) => updateIdentity(option, 'title')}
          />
        </div>
        <div className={classes.fieldPair}>
          <TextInput
            name='firstName'
            dataQsId='reviewPayee-firstName'
            label={payeeLang.firstNameLabel}
            value={value.firstName}
            useFormik={false}
            onChange={(fieldValue: string) => updateIdentity(fieldValue, 'firstName')}
          />
          <TextInput
            required
            name='lastName'
            dataQsId='reviewPayee-lastName'
            label={payeeLang.lastNameLabel}
            value={value.lastName}
            errorMessage={lastNameError}
            useFormik={false}
            onChange={(fieldValue: string) => updateIdentity(fieldValue, 'lastName')}
          />
        </div>
      </>
    )
    : (
      <>
        <TextInput
          required
          name='companyName'
          dataQsId='reviewPayee-companyName'
          label={payeeLang.companyNameLabel}
          value={value.companyName}
          errorMessage={companyNameError}
          useFormik={false}
          onChange={(fieldValue: string) => updateIdentity(fieldValue, 'companyName')}
        />
        <TextInput
          name='companyNameAffix'
          dataQsId='reviewPayee-companyNameAffix'
          label={payeeLang.companyNameAffixLabel}
          value={value.companyNameAffix}
          useFormik={false}
          onChange={(fieldValue: string) => updateIdentity(fieldValue, 'companyNameAffix')}
        />
      </>
    )

  const roleMismatchHint = !roleFitsDirection(value.role) && (
    <div className={classes.mismatch} data-testid='review-payee-role-mismatch'>
      <InfoOutlinedIcon className={clsx(classes.bannerIcon, classes.bannerIconInfo)} />
      <span>
        {directionHint}
        {value.role === 'privateCustomer' && recognisedCompany
          ? payeeLang.privateCustomerCompanyHint(recognisedCompany)
          : ''}
      </span>
    </div>
  )

  return (
    <div className={classes.root} data-qs-id='reviewPayeeSection'>
      <div className={classes.sectionTitle}>{payeeLang.sectionTitle}</div>

      <div className={clsx(
        classes.banner,
        banner.variant === 'warning' ? classes.bannerWarning : classes.bannerInfo,
      )} data-testid='review-payee-banner'>
        <InfoOutlinedIcon className={clsx(
          classes.bannerIcon,
          banner.variant === 'warning' ? classes.bannerIconWarning : classes.bannerIconInfo,
        )} />
        <span><strong>{banner.headline}</strong> {banner.body}</span>
      </div>

      {/* Rendered outside the card, so a mismatching role is stated in the resting state too —
          it is a verdict about the contact itself, not a hint that only applies while editing. */}
      {roleMismatchHint}

      <div className={classes.card}>
        {isEditing
          ? (
            <div className={classes.cardBody} onKeyDown={handleCardKeyDown}>
              <ReviewRadioInput
                dataQsId='reviewPayeeRole'
                name='reviewPayeeRole'
                value={value.role}
                options={ROLE_ORDER.map(role => ({
                  value: role,
                  // A role the derived direction does not support keeps working, but says so.
                  label: roleFitsDirection(role) ? ROLE_LABELS[role] : `${ROLE_LABELS[role]} *`,
                }))}
                onChange={selectRole}
              />
              {identityFields}
              <TextInput
                name='street'
                dataQsId='reviewPayee-street'
                label={lang.streetFieldLabel}
                value={value.address.street}
                useFormik={false}
                onChange={(fieldValue: string) => updateAddress({ street: fieldValue })}
              />
              <ZipCodeInput
                dataQsId='reviewPayee-zipCodeCity'
                zipCode={value.address.zipCode}
                city={value.address.city}
                useFormik={false}
                onChange={({ zipCode, city }) => updateAddress({ zipCode, city })}
              />
              <CountryInput
                dataQsId='reviewPayee-country'
                menuPlacement='top'
                maxMenuHeight={260}
                value={value.address.countryIso || config.defaultCountryAsISO2}
                includeCodeInLabel
                useFormik={false}
                onChange={(option) => updateAddress({ countryIso: option ? option.iso2 : '' })}
              />
            </div>
          )
          : (
            <div
              className={clsx(classes.display, readOnly && classes.displayReadOnly)}
              data-qs-id='reviewPayeeDisplay'
              data-testid='review-payee-display'
              role={readOnly ? undefined : 'button'}
              tabIndex={readOnly ? undefined : 0}
              onClick={() => !readOnly && setExpanded(true)}
              onKeyDown={(event) => {
                if (readOnly) return
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setExpanded(true)
                }
              }}
            >
              <div className={classes.displayLines}>
                <span className={clsx(!displayName && classes.displayPlaceholder)}>
                  {displayName || payeeLang.noName}
                </span>
                {addressLines.map(line => <span key={line}>{line}</span>)}
                {/* The role's required field decides which master record can be created —
                    say so here too, or the collapsed card hides the reason a save fails. */}
                {!validation.isValid && (
                  <span className={classes.displayError} data-testid='review-payee-required'>
                    {companyNameError || lastNameError}
                  </span>
                )}
              </div>
              {!readOnly && (
                <div className={classes.displayEditCell}>
                  <EditOutlinedIcon fontSize='small' />
                </div>
              )}
            </div>
          )}
      </div>

      <div>
        <div className={classes.assignLabel}>{payeeLang.assignLabel}</div>
        <SelectInput
          name='reviewPayeeDirectory'
          dataQsId='reviewPayeeDirectory'
          value={selectedOption}
          allowCreate={false}
          infiniteScroll
          useFormik={false}
          readOnly={readOnly}
          handleChange={handleDirectoryChange}
          customSelectOptions={{
            loadOptions: loadDirectoryOptions,
            isClearable: true,
            escapeClearsValue: true,
            placeholder: payeeLang.assignPlaceholder,
            labelSuffixDelimiter: ' ',
            labelPrefixDelimiter: ' - ',
          }}
        />
        <div className={classes.assignHint}>
          <ErrorOutlineRoundedIcon className={classes.assignHintIcon} />
          <span>{payeeLang.assignHint}</span>
        </div>
      </div>
    </div>
  )
}

export default ReviewPayeeSection
